import { useLocation } from 'preact-iso'
import { AlertTriangle, Check } from 'lucide-preact'
import type { WebCardDto } from '@/types/campaign'
import { getWebTypeMeta } from '@/constants/webTypes'
import { summariseOutstanding, isTitleOutstanding } from '@/constants/signOffReadiness'
import { LeadGoalBar } from '@/components'
import { formatDateOnly } from '@/utils/dateTimeFormatters'
import { shortAgo } from '@/utils/time'
import { routes } from '@/utils/routes'
import { DataGrid, type ColumnConfig } from '@/components/common/grids'
import { BRAND_CLASSES } from '@/constants/theme'

interface CampaignWebsListViewProps {
    webs: WebCardDto[]
    campaignId: string
    workspaceId: string
    clientCompanyId: string
}

const getLaunchStatus = (launchDate: string): { text: string; variant: 'past' | 'today' | 'future' } => {
    if (!launchDate) return { text: '—', variant: 'past' }
    
    // Get today's date as YYYY-MM-DD in local time
    const today = new Date()
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    
    if (launchDate === todayStr) {
        return { text: 'Today', variant: 'today' }
    } else if (launchDate < todayStr) {
        return { text: formatDateOnly(launchDate), variant: 'past' }
    } else {
        return { text: formatDateOnly(launchDate), variant: 'future' }
    }
}

export function CampaignWebsListView({ webs, campaignId, workspaceId, clientCompanyId }: CampaignWebsListViewProps) {
    const location = useLocation()

    const handleRowClick = (web: WebCardDto) => {
        const url = routes.scoped(clientCompanyId, workspaceId).web(campaignId, web.Id)
        location.route(url)
    }

    const columns: ColumnConfig<WebCardDto>[] = [
        {
            key: 'title',
            header: 'Web Title',
            sortable: true,
            accessor: (web) => web.Title,
            render: (web) => (
                <div className="flex items-center gap-2">
                    {/* A web created before the content call has no title yet, and the API's GetTitle()
                        falls back to the internal reference name. Showing that in mono and marking it
                        keeps a planning code from reading as if it were the public title. */}
                    {isTitleOutstanding(web.OutstandingForSignOff) ? (
                        <span className="flex items-baseline gap-1.5 min-w-0">
                            <span className="font-mono text-[13px] text-slate-400 dark:text-slate-500 truncate">{web.Name}</span>
                            <span className="text-xs text-slate-400 dark:text-slate-500 flex-shrink-0">— title not set</span>
                        </span>
                    ) : (
                        <span className="font-medium text-slate-900 dark:text-slate-100">
                            {web.Title}
                        </span>
                    )}
                    {web.ConferenceNotFound && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${BRAND_CLASSES.STATUS.SYNC_ERROR}`}>
                            Sync Error
                        </span>
                    )}
                </div>
            ),
        },
        {
            key: 'type',
            header: 'Type',
            sortable: true,
            accessor: (web) => web.WebType.toLowerCase(),
            render: (web) => {
                const typeMeta = getWebTypeMeta(web.WebType)
                const Icon = typeMeta.Icon
                const chipClass = typeMeta.chipClass
                const displayLabel = web.WebType.trim()
                return (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${chipClass}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {displayLabel}
                    </span>
                )
            },
        },
        {
            key: 'launchDate',
            header: 'Launch Date',
            sortable: true,
            accessor: (web) => new Date(web.LaunchDate).getTime(),
            render: (web) => {
                const launchStatus = getLaunchStatus(web.LaunchDate)
                return (
                    <div className={`text-sm ${
                        launchStatus.variant === 'today' 
                            ? 'text-primary-600 dark:text-primary-400 font-semibold'
                            : launchStatus.variant === 'future'
                            ? 'text-slate-600 dark:text-slate-400'
                            : 'text-slate-500 dark:text-slate-500'
                    }`}>
                        {launchStatus.text}
                    </div>
                )
            },
        },
        {
            key: 'leads',
            header: 'Leads',
            sortable: true,
            accessor: (web) => web.LeadsCount || 0,
            render: (web) => {
                const goal = Math.max(0, web.LeadGoal || 0)
                const leads = Math.max(0, web.LeadsCount || 0)
                return (
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {leads.toLocaleString()} / {goal.toLocaleString()}
                    </div>
                )
            },
        },
        {
            key: 'progress',
            header: 'Progress',
            sortable: false,
            render: (web) => {
                const goal = Math.max(0, web.LeadGoal || 0)
                const leads = Math.max(0, web.LeadsCount || 0)
                return (
                    <div className="w-32">
                        <LeadGoalBar sourced={leads} goal={goal} />
                    </div>
                )
            },
        },
        {
            key: 'beforeSignOff',
            header: 'Before Sign-Off',
            sortable: true,
            // Undefined means the response predates this field, which is not the same as "nothing
            // outstanding" — sort those last rather than letting them read as ready.
            accessor: (web) => web.OutstandingForSignOff ? web.OutstandingForSignOff.length : Number.MAX_SAFE_INTEGER,
            render: (web) => {
                const outstanding = web.OutstandingForSignOff
                if (!outstanding) return null
                if (outstanding.length === 0) {
                    return (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300 border border-green-200 dark:border-green-800">
                            <Check className="w-3 h-3 flex-shrink-0" />
                            Ready
                        </span>
                    )
                }
                return (
                    // Hovering names the items; clicking falls through to the row, which opens the web.
                    // In a list you are triaging, and the next thing you do is go work on the one that is
                    // behind — a popover you then have to navigate past is a wasted step.
                    <span
                        title={summariseOutstanding(outstanding)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                    >
                        <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                        {outstanding.length} to go
                    </span>
                )
            },
        },
        {
            key: 'lastLead',
            header: 'Last Lead',
            sortable: true,
            accessor: (web) => web.LastLeadCreatedDate ? new Date(web.LastLeadCreatedDate).getTime() : 0,
            render: (web) => (
                <div className="text-sm text-slate-600 dark:text-slate-400">
                    {web.LastLeadCreatedDate ? shortAgo(web.LastLeadCreatedDate) : 'No leads'}
                </div>
            ),
        },
    ]

    const getSortComparator = (key: string) => {
        return (a: WebCardDto, b: WebCardDto) => {
            const column = columns.find(col => col.key === key)
            if (!column?.accessor) return 0

            const aValue = column.accessor(a)
            const bValue = column.accessor(b)

            // Handle null/undefined - sort to the end
            if (aValue == null && bValue == null) return 0
            if (aValue == null) return 1
            if (bValue == null) return -1

            if (aValue < bValue) return -1
            if (aValue > bValue) return 1
            return 0
        }
    }

    return (
        <DataGrid
            data={webs}
            columns={columns}
            getRowKey={(web) => web.Id}
            onRowClick={handleRowClick}
            getSortComparator={getSortComparator}
            defaultSortKey="launchDate"
            defaultSortDirection="desc"
            emptyMessage="No webs found"
            pagination={true}
            pageSize={10}
        />
    )
}
